<template>
  <VcLoaderOverlay v-if="showLoadingSpinner" class="cart-spinner" />
  <div v-if="initialConfigurationLoaded && canBeLoaded">
    <div ref="root" class="form-group">
      <div class="flex flex-col xl:flex-row">
        <div class="flex w-full flex-col gap-5">
          <div class="flex flex-col gap-2.5">
            <VcLabel required for-id="cardNumber-container"> {{ labels.number }}</VcLabel>
            <div
              :class="[
                'cyber-source-input-wrap form-control flex items-center',
                { 'flex-microform-invalid': displayCardNumberError },
              ]"
            >
              <VcIcon name="credit-card-alt" class="order-1 mr-2.5 text-[--color-neutral-a2]" :size="20"></VcIcon>
              <div id="cardNumber-container" class="order-2"></div>
            </div>
            <VcInputDetails
              :message="displayCardNumberError ? cardNumberErrorMessage : ''"
              :error="displayCardNumberError"
              class="capitalize"
            />
          </div>

          <div class="flex flex-col gap-x-6 gap-y-3 sm:flex-row">
            <VcInput
              v-model="expirationDate"
              :label="labels.expirationDate"
              :placeholder="labels.datePlaceholder"
              :message="displayExpirationDateErrors ? expirationDateErrors : ''"
              :error="displayExpirationDateErrors"
              :disabled="disabled"
              name="expirationDate"
              autocomplete="off"
              minlength="7"
              maxlength="7"
              class="w-full capitalize md:w-1/2"
              required
              test-id-input="expiration-date-input"
              @blur="hasExpirationDateBeenBlurred = true"
            />

            <div class="flex flex-col gap-2.5">
              <VcLabel required for-id="securityCode-container">{{ labels.securityCode }} (CVV)</VcLabel>
              <div
                :class="[
                  'form-control cyber-source-input-wrap flex items-center justify-between',
                  { 'flex-microform-invalid': displayCvvError },
                ]"
              >
                <div id="securityCode-container"></div>

                <VcTooltip placement="top" hover width="max-content">
                  <template #trigger>
                    <VcIcon name="icon-question-circle" :size="16"></VcIcon>
                  </template>

                  <template #content>
                    {{ $t("pages.account.payments.tooltip_message") }}
                  </template>
                </VcTooltip>
              </div>
              <VcInputDetails
                :message="displayCvvError ? cvvErrorMessage : ''"
                :error="displayCvvError"
                class="capitalize"
              />
            </div>
          </div>
          <VcInput
            v-model.trim="cardholderName"
            :placeholder="$t('shared.payment.bank_card_form.cardholder_name_label')"
            :label="labels.cardholderName"
            :message="displayCardholderNameErrors ? formErrors.cardholderName : ''"
            :error="displayCardholderNameErrors"
            :disabled="props.disabled"
            required
            test-id-input="card-holder-input"
            class="capitalize"
            @blur="hasCardholderNameBeenBlurred = true"
          />
        </div>
      </div>
    </div>

    <input id="flexresponse" type="hidden" name="flexresponse" />
    <!-- <div class="mt-6 flex flex-col items-center gap-x-6 gap-y-4 md:flex-row xl:mt-8"></div> -->
    <div v-if="!props.isDefault" class="mt-5 flex flex-col gap-4">
      <VcCheckbox
        v-if="props.isCheckout"
        id="savePayment"
        v-model="isSavePaymentSelected"
        name="savePayment"
        class="rounded-md text-base font-medium"
        label="savePayment"
      >
        {{ $t("shared.payment.bank_card_form.save_credit_card") }}
      </VcCheckbox>
      <VcCheckbox
        v-if="showSetAsDefaultPaymentCard"
        id="setAsDefaultPaymentCard"
        v-model="setAsDefaultPaymentCard"
        name="setAsDefaultPaymentCard"
        class="rounded-md text-base font-medium"
        label="setAsDefaultPaymentCard"
      >
        {{ $t("shared.payment.bank_card_form.set_as_default_payment") }}
      </VcCheckbox>
    </div>
    <p class="mt-5 text-sm font-medium italic leading-[22px]">
      To verify your new payment card, it may be authorized for a small amount. This temporary authorization will be
      reversed after 7 business days.
    </p>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { useCssVar } from "@vueuse/core";
import { omit } from "lodash";
import { useForm, useResetForm } from "vee-validate";
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { createAuthenticationSetup, initializeCartPayment, logClientError } from "@/core/api/graphql";
import { useCurrency, useDataAreaId, useFooterBlock } from "@/core/composables";
import { AddressType } from "@/core/enums";
import { Logger } from "@/core/utilities";
import { useUser, useUserPayments } from "@/shared/account";
import {
  getInvalidCardMessageByCurrency,
  getNonDefaultPaymentInstrumentsWithSameDataAreaId,
} from "@/shared/account/utils/paymentUtils";
import { useFullCart } from "@/shared/cart";
import { useDeviceDataCollection } from "@/shared/payment/composables/useDeviceDataCollection";
import { usePayerAuthentication } from "@/shared/payment/composables/usePayerAuthentication";
import { eventBus } from "@/shared/thorlabs/shared";
import { cardTypesMap } from "../constants";
import {
  analyzeCyberSourceError,
  getCyberSourceErrorMessage,
  isCardTypeValidForCurrency,
  shouldReinitPaymentForm,
} from "../utils";
import type {
  CartAddressTypeExtension,
  KeyValueType,
  MemberAddressTypeExtension,
  OrderAddressTypeExtension,
  PaymentInstrumentDetailsType,
} from "@/core/api/graphql/types";
import type { Ref } from "vue";

interface IProps {
  disabled?: boolean;
  isCheckout?: boolean;
  card?: PaymentInstrumentDetailsType;
  isDefault?: boolean;
  isPartOfModal?: boolean;
}

interface IEmits {
  (event: "cardSaved"): void;
}

interface IField {
  load(containerId: string): void;
  on(event: string, callback: (data: { valid: boolean; card: Array<unknown> }) => void): void;
}

interface IMicroform {
  createField(type: "number" | "securityCode", options?: IFieldOptions): IField;
  createToken(options: Record<string, unknown>, callback: (err: unknown, token: string) => void): void;
}

interface IFieldOptions {
  placeholder?: string;
  maxLength?: number;
}

type FlexConstructorType = {
  new (key: string): IFlex;
};

interface IFlex {
  microform(options: { styles: Record<string, unknown> }): IMicroform;
}

interface ISaveCard {
  address: MemberAddressTypeExtension | undefined;
  paymentInstrumentId: string | undefined;
  paymentInstrumentOriginalDataAreaId: string | undefined;
  isDefault: boolean | undefined;
  captchaCode: string;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  isCheckout: false,
  isPartOfModal: false,
});

declare let Flex: FlexConstructorType;

let flex: IFlex;
let microform: IMicroform;

const { t } = useI18n();
const { cart } = useFullCart();
const { user } = useUser();
const { addPayment, updatePaymentInstrument, payments } = useUserPayments();
const { billingDataAreaId, getDataAreaId } = useDataAreaId();
const { currentCurrency } = useCurrency();
const { performDeviceDataCollection } = useDeviceDataCollection();
const { is3DSPayerAuthenticationEnabled } = usePayerAuthentication();
const loading = ref(false);
const kid = ref("");
const initialConfigurationLoaded = ref(false);
const currentDataAreaId = ref("");
const transientToken = ref("");
const labels = computed(() => {
  return {
    number: t("shared.payment.bank_card_form.number_label"),
    cardholderName: t("shared.payment.bank_card_form.cardholder_name_label"),
    expirationDate: t("shared.payment.bank_card_form.expiration_date_label"),
    yearLabel: t("shared.payment.bank_card_form.year_label"),
    datePlaceholder: t("shared.payment.bank_card_form.expiration_date_placeholder_extended"),
    monthLabel: t("shared.payment.bank_card_form.month_label"),
    securityCode: t("shared.payment.bank_card_form.security_code_label"),
  };
});

const monthYupSchema = yup
  .string()
  .required()
  .length(2, t("shared.payment.bank_card_form.month_digits"))
  .matches(/^(0?[1-9]|1[0-2])$/, t("shared.payment.bank_card_form.month_error"))
  .label(labels.value.monthLabel);

const validationSchema = toTypedSchema(
  yup.object({
    number: yup.string().required().max(19).label(labels.value.number),
    cardholderName: yup
      .string()
      .required()
      .max(64)
      .matches(/^[^\s]+(\s+[^\s]+)+$/, t("shared.payment.bank_card_form.cardholder_full_name_required"))
      .label(labels.value.cardholderName),
    month: monthYupSchema,
    year: yup.string().when("month", ([month], schema) => {
      return monthYupSchema.isValidSync(month)
        ? schema
            .length(2, t("shared.payment.bank_card_form.year_digits"))
            .matches(/^\d{2}$/, t("shared.payment.bank_card_form.year_error"))
            .label(labels.value.yearLabel)
        : schema;
    }),
  }),
);
const initialValues = {
  cardholderName: "",
  month: "",
  year: "",
};

const {
  values: formValues,
  errors: formErrors,
  defineField,
} = useForm({
  validationSchema,
  initialValues,
});

const resetForm = useResetForm();

function resetFormState() {
  resetForm({
    values: {
      cardholderName: "",
      month: "",
      year: "",
    },
  });
  transientToken.value = "";
  cybersourceMeta.value.cardNumber.valid = false;
  cybersourceMeta.value.cvv.valid = false;
  isSavePaymentSelected.value = false;
  setAsDefaultPaymentCard.value = false;
}
const cybersourceMeta = ref({
  cardNumber: {
    valid: false,
  },
  cvv: {
    valid: false,
  },
});

const canBeLoaded = ref(false);
const cardNumberErrorMessage = ref("");
const cvvErrorMessage = ref("");
const hasCardNumberBeenBlurred = ref(false);
const hasCvvBeenBlurred = ref(false);
const hasExpirationDateBeenBlurred = ref(false);
const hasCardholderNameBeenBlurred = ref(false);
const displayCardNumberError = computed(() => {
  return hasCardNumberBeenBlurred.value && !!cardNumberErrorMessage.value;
});
const displayCvvError = computed(() => {
  return hasCvvBeenBlurred.value && !!cvvErrorMessage.value;
});
const displayExpirationDateErrors = computed(() => {
  return hasExpirationDateBeenBlurred.value && !!expirationDateErrors.value;
});

const displayCardholderNameErrors = computed(() => {
  return hasCardholderNameBeenBlurred.value && !!formErrors.value.cardholderName;
});

const isValidBankCard = computed(() => {
  return (
    cybersourceMeta.value.cardNumber.valid &&
    cybersourceMeta.value.cvv.valid &&
    !formErrors.value.month &&
    !formErrors.value.year &&
    !formErrors.value.cardholderName
  );
});

const [cardholderName] = defineField("cardholderName");
const [month] = defineField("month");
const [year] = defineField("year");

const expirationDate = computed({
  get() {
    const mm = month.value ?? "";
    const yy = year.value ?? "";
    if (!mm && !yy) {
      return "";
    }
    if (mm.length < 2 && !yy) {
      return mm;
    }
    return `${mm.padEnd(2, " ")} / ${yy}`;
  },
  set(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 4); // max 4 digits

    if (digits.length === 0) {
      month.value = "";
      return;
    }

    if (digits.length <= 2) {
      month.value = digits;
      year.value = "";
    } else {
      month.value = digits.slice(0, 2);
      year.value = digits.slice(2);
    }
  },
});

const expirationDateErrors = computed<string>(() =>
  [formErrors.value.month, formErrors.value.year].filter(Boolean).join(". "),
);
const isSavePaymentSelected = ref(false);
const setAsDefaultPaymentCard = ref(false);
const referenceId = ref<string>("");

const getParameters = (maskedCardNumber: string, cardType: string, dataAreaId?: string) => {
  return [
    {
      key: "expirationDate",
      value: `${formValues.month}-20${formValues.year}`,
    },
    {
      key: "maskedCardNumber",
      value: maskedCardNumber,
    },
    {
      key: "cardType",
      value: cardType,
    },
    {
      key: "token",
      value: transientToken.value,
    },
    {
      key: "savePayment",
      value: isSavePaymentSelected.value ? "true" : "false",
    },
    {
      key: "dataAreaId",
      value: dataAreaId ?? currentDataAreaId.value,
    },
    {
      key: "captureCtxKId",
      value: kid.value,
    },
    {
      key: "cardholderName",
      value: formValues.cardholderName,
    },
    {
      key: "referenceId",
      value: referenceId.value,
    },
  ];
};

async function saveCreditCard(payload: ISaveCard) {
  loading.value = true;
  try {
    if (!isValidBankCard.value) {
      return;
    } else {
      const addressInvalidMessage = getAddressInvalidMessage(payload.address);
      if (addressInvalidMessage) {
        eventBus.emit("showCreditCardFormError", {
          message: t(addressInvalidMessage),
        });
        return;
      }
    }
    transientToken.value = await createToken({
      cardholderName: formValues.cardholderName,
      expirationMonth: formValues.month,
      expirationYear: `20${formValues.year}`,
    });
    const parameters: KeyValueType[] = [];
    parameters.push(
      {
        key: "token",
        value: transientToken.value,
      },
      {
        key: "savePayment",
        value: "true",
      },
      {
        key: "isDefaultPayment",
        value: setAsDefaultPaymentCard.value ? "true" : "false",
      },
      {
        key: "customerId",
        value: user.value?.id ?? "",
      },
      {
        key: "cardholderName",
        value: formValues.cardholderName,
      },
      {
        key: "dataAreaId",
        value: currentDataAreaId.value,
      },
      {
        key: "captureCtxKId",
        value: kid.value,
      },
      {
        key: "billingAddressId",
        value: payload?.address?.id ?? "",
      },
    );
    if (payload.paymentInstrumentId && payload.paymentInstrumentOriginalDataAreaId) {
      await updatePaymentInstrument(
        payload.paymentInstrumentId,
        payload.paymentInstrumentOriginalDataAreaId,
        parameters,
        {
          ...omit(payload.address, [
            "id",
            "isDefault",
            "description",
            "createdDate",
            "isFavorite",
            "isDefaultShippingAddressTL",
            "isDefaultBillingAddressTL",
            "modifiedDate",
          ]),
          addressType: AddressType.Billing,
        },
        payload.isDefault,
        payload.captchaCode,
      ).then(async (value) => {
        if (!value?.isSuccess) {
          Logger.error(saveCreditCard.name, value?.errorMessage);
          const errorResult = analyzeCyberSourceError(value.errorMessage);
          eventBus.emit("showCreditCardFormError", {
            message: getCyberSourceErrorMessage(errorResult, t),
          });
          if (shouldReinitPaymentForm(errorResult)) {
            await initPayment();
          }
        } else {
          emit("cardSaved");
          resetFormState();
        }
      });
    } else {
      await addPayment(
        parameters,
        {
          ...omit(payload.address, [
            "id",
            "isDefault",
            "description",
            "createdDate",
            "isFavorite",
            "isDefaultShippingAddressTL",
            "isDefaultBillingAddressTL",
            "modifiedDate",
          ]),
          addressType: AddressType.Billing,
        },
        payload.captchaCode,
      ).then(async (value) => {
        if (!value?.isSuccess) {
          Logger.error(saveCreditCard.name, value?.errorMessage);
          const errorResult = analyzeCyberSourceError(value.errorMessage);
          eventBus.emit("showCreditCardFormError", {
            message: getCyberSourceErrorMessage(errorResult, t),
          });
          if (shouldReinitPaymentForm(errorResult)) {
            await initPayment();
          }
        } else {
          emit("cardSaved");
          resetFormState();
        }
      });
    }
  } catch (e) {
    try {
      logErrorInBackend("CyberSource Create Token Error", e);
      const errorResult = analyzeCyberSourceError(e);
      eventBus.emit("showCreditCardFormError", {
        message: getCyberSourceErrorMessage(errorResult, t),
      });
      if (shouldReinitPaymentForm(errorResult)) {
        await initPayment();
      }
      Logger.error(saveCreditCard.name, e);
    } catch (loggingError) {
      Logger.error(saveCreditCard.name, loggingError);
      eventBus.emit("showCreditCardFormError", {
        message: t("shared.payment.bank_card_form.default_authorization_error_modal"),
      });
      await initPayment();
    }
  } finally {
    loading.value = false;
  }
}

async function createToken(options: Record<string, unknown>): Promise<string> {
  return new Promise((resolve, reject) => {
    microform.createToken(options, (err: unknown, token: string) => {
      if (err) {
        logErrorInBackend("CyberSource Create Token Error", err);
        reject(err);
      } else {
        resolve(token);
      }
    });
  });
}

function getAddressInvalidMessage(
  address: MemberAddressTypeExtension | CartAddressTypeExtension | OrderAddressTypeExtension | undefined,
  defaultErrorKey: string = "shared.payment.bank_card_form.default_authorization_error_modal",
): string | null {
  if (!address) {
    return defaultErrorKey;
  }
  if (!address.phone) {
    return "shared.payment.bank_card_form.invalid_phone_number";
  }
  if (!address.email) {
    return "shared.payment.bank_card_form.invalid_billing_address_email";
  }
  return null;
}

function initErrorState() {
  canBeLoaded.value = false;
  cardNumberErrorMessage.value = "";
  cvvErrorMessage.value = "";
  hasCardNumberBeenBlurred.value = false;
  hasCvvBeenBlurred.value = false;
  hasExpirationDateBeenBlurred.value = false;
  hasCardholderNameBeenBlurred.value = false;
}

async function loadPaymentForm(dataAreaId?: string) {
  loading.value = true;
  await initPayment(dataAreaId);
}

async function initPayment(dataAreaId?: string) {
  try {
    loading.value = true;
    initialConfigurationLoaded.value = true;

    if (kid.value) {
      removeScript();
      initErrorState();
    }
    // Retries after a failed save must stay on the tenant the form was already loaded for (e.g. FRA), not reset to MUC.
    const localBillingDataAreaId =
      dataAreaId ??
      (currentDataAreaId.value ||
        getDataAreaId(currentCurrency.value.code, currentCurrency.value.code === "EUR" ? "MUC" : "") ||
        billingDataAreaId.value);
    currentDataAreaId.value = localBillingDataAreaId;
    const response = await initializeCartPayment(cart.value?.id ?? "", localBillingDataAreaId);

    if (!response.isSuccess) {
      handleInitPaymentError(response.errorMessage);
      loading.value = false;
      return;
    }

    const publicParameters = response.publicParameters;
    const scriptUrl = getValue(publicParameters, "clientScript");

    if (!publicParameters || !scriptUrl) {
      loading.value = false;
      return;
    }

    const clientLibraryIntegrity = getValue(publicParameters, "clientLibraryIntegrity");
    canBeLoaded.value = true;
    await useDynamicScript(scriptUrl, clientLibraryIntegrity);
    await initFlex(getValue(publicParameters, "jwt")!);
    initForm();

    kid.value = getValue(publicParameters, "kid") ?? "";
  } catch (e) {
    logErrorInBackend("CyberSource Init Payment Error", e);
    handleInitPaymentError(e);
    loading.value = false;
  }
}

function handleInitPaymentError(error?: unknown) {
  Logger.error(initPayment.name, error);

  let errorMessage = t("shared.payment.bank_card_form.default_payment_processor_unavailable");
  if (!props.isPartOfModal) {
    const footerBlockComposable = useFooterBlock();
    const htmlPhone = `<a href="tel:${footerBlockComposable?.footerBlock.value?.salesPhone ?? ""}" class='text-[--color-link]'>${footerBlockComposable?.footerBlock.value?.salesPhone ?? ""}</a>`;

    errorMessage = t("shared.payment.bank_card_form.payment_processor_unavailable_with_phone_number", [htmlPhone]);
  }
  eventBus.emit("showCreditCardFormError", {
    message: errorMessage,
  });
}

function logErrorInBackend(errorTitle: string, e: unknown) {
  const userIdentifier = user.value?.memberId ?? user.value?.id ?? "unknown user";
  const errorResult = analyzeCyberSourceError(e);
  const errorMessage = getCyberSourceErrorMessage(errorResult, t);
  const isDefaultErrorMessage = errorMessage === t("shared.payment.bank_card_form.default_authorization_error_modal");
  const baseErrorContent = isDefaultErrorMessage
    ? e instanceof String
      ? e
      : (e as { message?: string }).message ?? (e as Error).message ?? "Unknown error"
    : errorMessage;

  void logClientError({
    errorTitle,
    errorContent: `${baseErrorContent as string}`,
    stackTrace: e instanceof Error ? e.stack ?? "No stack trace available" : "No stack trace available",
    userId: userIdentifier,
  }).catch(() => {});
}

function initForm() {
  const number = microform.createField("number", { placeholder: "Card Number" });
  const securityCode = microform.createField("securityCode", {
    placeholder: "CVV",
    maxLength: 4,
  });
  number.load("#cardNumber-container");
  number.on("change", onChange);
  number.on("blur", () => {
    hasCardNumberBeenBlurred.value = true;
  });
  securityCode.load("#securityCode-container");
  securityCode.on("change", onChange);
  securityCode.on("blur", () => {
    hasCvvBeenBlurred.value = true;
  });
  let name = "";
  if (props.card) {
    name = `${props.card.cardholderFirstName} ${props.card.cardholderLastName}`;
    cardholderName.value = name ?? "";
    month.value = props.card?.expirationMonth ?? "";
    year.value = props.card?.expirationYear.slice(2, 4) ?? "";
  } else {
    if (cardholderName.value) {
      cardholderName.value = "";
    }
    if (month.value) {
      month.value = "";
    }
    if (year.value) {
      year.value = "";
    }
  }

  loading.value = false;
}

const getCardTypeFromCybsMetadata = (card: Array<unknown>): string => {
  const cardInfo = card[0] as { cybsCardType: string };
  return cardInfo.cybsCardType;
};

const checkCardTypeValidityByCurrency = (cardType: string): boolean => {
  return !!cardType && !isCardTypeValidForCurrency(cardTypesMap[cardType], currentCurrency.value.code);
};

function onChange(data: { valid: boolean; card: Array<unknown>; empty?: boolean }) {
  if (data.card) {
    cybersourceMeta.value.cardNumber.valid = data.valid;
    if (data.empty) {
      cardNumberErrorMessage.value = t("common.messages.required_field");
    } else {
      if (!data.valid) {
        cardNumberErrorMessage.value = t("common.messages.invalid_card_number");
      } else {
        if (data.card.length > 0) {
          const cardType = getCardTypeFromCybsMetadata(data.card);
          const isInvalidCardType = checkCardTypeValidityByCurrency(cardType);
          if (isInvalidCardType) {
            cardNumberErrorMessage.value = getInvalidCardMessageByCurrency(currentCurrency.value.code, t);
            cybersourceMeta.value.cardNumber.valid = false;
          } else {
            cardNumberErrorMessage.value = "";
          }
        }
      }
    }
  } else {
    cybersourceMeta.value.cvv.valid = data.valid;
    if (data.empty) {
      cvvErrorMessage.value = t("common.messages.required_field");
    } else {
      if (!data.valid) {
        cvvErrorMessage.value = t("common.messages.invalid_value");
      } else {
        cvvErrorMessage.value = "";
      }
    }
  }
}

async function createTokenWithValidationPayload(
  address: MemberAddressTypeExtension | CartAddressTypeExtension | OrderAddressTypeExtension,
) {
  loading.value = true;
  try {
    if (!isValidBankCard.value) {
      return;
    }
    const addressInvalidMessage = getAddressInvalidMessage(
      address,
      "shared.payment.bank_card_form.default_authorization_error_checkout",
    );
    if (addressInvalidMessage) {
      eventBus.emit("showCreditCardFormError", {
        message: t(addressInvalidMessage),
      });
      return;
    }
    transientToken.value = await createToken({
      cardholderName: formValues.cardholderName,
      expirationMonth: formValues.month,
      expirationYear: `20${formValues.year}`,
    });

    const dataAreaId = getDataAreaId(currentCurrency.value.code, address.countryCode ?? "") ?? billingDataAreaId.value;
    if (is3DSPayerAuthenticationEnabled(dataAreaId)) {
      try {
        const setupResult = await createAuthenticationSetup(
          transientToken.value,
          dataAreaId,
          undefined,
          user.value?.id ?? "",
        );
        await performDeviceDataCollection(setupResult);

        if (setupResult.referenceId) {
          referenceId.value = setupResult.referenceId;
        }
      } catch (e) {
        Logger.error("createAuthenticationSetup", e);
      }
    }

    return {
      cardholderName: formValues.cardholderName,
      token: transientToken.value,
      kid: kid.value,
    };
  } catch (e) {
    logErrorInBackend("CyberSource Create Token Error", e);
    const errorResult = analyzeCyberSourceError(e);

    eventBus.emit("showCreditCardFormError", {
      message: getCyberSourceErrorMessage(errorResult, t),
    });

    if (shouldReinitPaymentForm(errorResult)) {
      eventBus.emit("expandCheckoutComponent", { initPaymentForm: true });
    }
    Logger.error(createTokenWithValidationPayload.name, e);
  } finally {
    loading.value = false;
  }
}

const root = ref(null);

// custom font-family like Lato not supported
const customStyles = {
  input: {
    "font-size": "1rem",
    "font-family": "Manrope,sans-serif",
    color: useCssVar("--body-text-color", root).value,
  },
};

async function initFlex(key: string) {
  try {
    flex = new Flex(key);
    microform = await createMicroformAsync({
      styles: customStyles,
    });
  } catch (e) {
    Logger.error(initFlex.name, e);
  }
}

function createMicroformAsync(options: { styles: Record<string, unknown> }): Promise<IMicroform> {
  return new Promise((resolve, reject) => {
    try {
      const microformInstance = flex.microform(options);
      resolve(microformInstance);
    } catch (error: unknown) {
      reject(error instanceof Error ? error : new Error("Failed to create microform"));
    }
  });
}

function getValue(publicParameters?: KeyValueType[], key?: string) {
  return publicParameters?.find((x) => x.key === key)?.value;
}

let scriptTag: Ref<HTMLScriptElement | null>;

async function useDynamicScript(url: string, integrity?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;

    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = "anonymous";
    }

    script.onload = () => {
      scriptTag = ref(script);
      resolve();
    };

    script.onerror = (error) => {
      const errorTitle = `Failed to load CyberSource script: ${url}`;
      logErrorInBackend(errorTitle, error);
      reject(new Error(errorTitle));
    };

    document.head.appendChild(script);
  });
}

function removeScript() {
  if (scriptTag?.value && scriptTag?.value?.parentNode) {
    scriptTag.value.parentNode.removeChild(scriptTag.value);
  }
}

const showSetAsDefaultPaymentCard = computed(() => {
  return (
    !props.isCheckout &&
    (!props.card ||
      (props.card &&
        !props.isCheckout &&
        !getNonDefaultPaymentInstrumentsWithSameDataAreaId(
          payments.value,
          props.card.paymentInstrumentTokenId,
          props.card.dataAreaId,
        )))
  );
});

const showLoadingSpinner = computed(() => {
  return loading.value;
});

eventBus.on("addCardClicked", async (payload) => {
  await saveCreditCard(payload);
});

eventBus.on("updateCardClicked", async (payload) => {
  await saveCreditCard(payload);
});

onUnmounted(() => {
  eventBus.off("addCardClicked");
  eventBus.off("updateCardClicked");
  removeScript();
});

defineExpose({
  isSavePaymentSelected,
  setAsDefaultPaymentCard,
  isValidBankCard,
  createTokenWithValidationPayload,
  initPayment,
  loadPaymentForm,
  initialConfigurationLoaded,
  currentDataAreaId,
  getParameters,
});

watch(props, (newProps) => {
  setAsDefaultPaymentCard.value = newProps.card ? newProps.card.isDefault : newProps.isDefault ?? false;
});
</script>

<style lang="scss">
.cyber-source-input-wrap {
  --base-color: var(--vc-input-base-color, var(--color-neutral-a2));
  --focus-color: rgb(from var(--base-color) r g b / 0.2);

  @apply h-[50px] text-base relative py-3 px-5 appearance-none bg-transparent border bg-additional-50 rounded-md leading-none w-full min-w-0;

  iframe {
    @apply h-[50px];
  }
  .vc-tooltip__content {
    &::before,
    &::after {
      @apply hidden;
    }
  }
}

.cyber-source-input-wrap.flex-microform-focused {
  @apply ring ring-1 ring-[--color-neutral-a2];
}

.cyber-source-input-wrap.flex-microform-invalid {
  --base-color: var(--color-danger-450);
  @apply border-[--base-color];
}

.payment-section {
  input {
    &::placeholder {
      @apply opacity-100 text-[--color-neutral-a2];
    }
  }
}
</style>
